<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { RouterView, useRoute, useRouter } from 'vue-router';
import AppNav from '@/app/AppNav.vue';
import AppTabBar from '@/app/AppTabBar.vue';
import SearchOverlay from '@/components/search/SearchOverlay.vue';
import { useAuthStore } from '@/stores/auth';
import { useBookingsStore } from '@/stores/bookings';
import { useMessagesStore } from '@/stores/messages';
import { useNotificationsStore } from '@/stores/notifications';
import { usePresenceStore } from '@/stores/presence';
import { setFormatLocale } from '@/lib/format';

const route = useRoute();
const router = useRouter();

/**
 * Drill-down pages (meta.slide) slide in from the right on a forward navigation and
 * back out to the right on a pop. Direction is derived from our own path stack rather
 * than `history.state.position`, which other code (e.g. the provider sheet's custom
 * pushState) overwrites — a "back" lands on the entry just beneath the top of stack.
 */
const transitionName = ref('');
const navStack: string[] = [route.fullPath];
router.afterEach((to, from) => {
  const goingBack = navStack.length >= 2 && navStack[navStack.length - 2] === to.fullPath;
  if (goingBack) navStack.pop();
  else if (navStack[navStack.length - 1] !== to.fullPath) navStack.push(to.fullPath);

  const animated = to.meta.slide || from.meta.slide;
  transitionName.value = animated ? (goingBack ? 'nav-back' : 'nav-forward') : '';
});

// Poll the unread badges and broadcast presence only while signed in.
const auth = useAuthStore();
const messages = useMessagesStore();
const notifications = useNotificationsStore();
const bookings = useBookingsStore();
const presence = usePresenceStore();
watch(
  () => auth.isAuthenticated,
  (on) => {
    if (on) {
      messages.start();
      notifications.start();
      bookings.start();
      presence.start();
    } else {
      messages.stop();
      notifications.stop();
      bookings.stop();
      presence.stop();
    }
  },
  { immediate: true },
);

// Format prices, money and times in the user's chosen language.
watch(
  () => auth.user?.locale,
  (locale) => setFormatLocale(locale),
  { immediate: true },
);
const onVisible = (): void => {
  if (!document.hidden && auth.isAuthenticated) {
    void messages.refresh();
    void notifications.refresh();
    void bookings.refresh();
    void presence.ping();
  }
};

/**
 * The search sheet rides on the `#search` hash: the page underneath stays
 * mounted behind the slide-up, and back (browser or hardware) closes it.
 */
const searchOpen = computed(() => route.hash === '#search');

// The sheet owns the screen while it is open; the page under it must not scroll.
watch(searchOpen, (open) => {
  document.documentElement.style.overflow = open ? 'hidden' : '';
});

/**
 * The listing detail page is immersive: its photo runs to the top edge and it
 * carries its own fixed back/favourite controls, so the topbar and tab bar are
 * hidden. A body class drops the tab bar's reserved bottom padding with them.
 */
const chromeless = computed(() => route.name === 'listing' || route.name === 'conversation');

/**
 * Auth screens (sign in / register / password flows) hide the tab bar too — they are
 * a dead end until you're in — but keep normal page padding (they are not the flush,
 * edge-to-edge immersive pages). `is-chromeless` drops the tab bar's reserved bottom
 * space whenever the bar is gone, for either reason.
 */
const AUTH_ROUTES = new Set(['login', 'register', 'forgot-password', 'reset-password', 'verify-email']);
const hideTabBar = computed(() => chromeless.value || AUTH_ROUTES.has(route.name as string));
watch(hideTabBar, (on) => document.body.classList.toggle('is-chromeless', on), { immediate: true });

/**
 * The topbar (app logo, theme toggle, hamburger) belongs only to home — it is the
 * app's front door. Every other page carries its own header and reaches the rest of
 * the app through the tab bar, so the topbar above them is redundant.
 */
const hideTopNav = computed(() => route.name !== 'home');

const online = ref(true);
const setOnline = () => (online.value = navigator.onLine);

onMounted(() => {
  setOnline();
  window.addEventListener('online', setOnline);
  window.addEventListener('offline', setOnline);
  document.addEventListener('visibilitychange', onVisible);
});
onUnmounted(() => {
  window.removeEventListener('online', setOnline);
  window.removeEventListener('offline', setOnline);
  document.removeEventListener('visibilitychange', onVisible);
});
</script>

<template>
  <p v-if="!online" class="offline" role="status">You are offline. Showing what we already had.</p>

  <AppNav v-if="!hideTopNav" />

  <main class="page" :class="{ 'page--flush': chromeless }">
    <RouterView v-slot="{ Component, route: slotRoute }">
      <!-- The wrapper gives <Transition> a single element to animate (pages may have
           fragment roots). Slide pages get a per-path key so the transition fires;
           everything else shares one key, so its wrapper persists (no animation, no
           warning) and only the inner page swaps. -->
      <Transition :name="transitionName" mode="out-in">
        <div :key="slotRoute.meta.slide ? slotRoute.path : 'app-page'" class="route-slot">
          <component :is="Component" />
        </div>
      </Transition>
    </RouterView>
  </main>

  <AppTabBar v-if="!hideTabBar" />

  <Transition name="sheet-up">
    <SearchOverlay v-if="searchOpen" />
  </Transition>
</template>
